import { LogOut, UserCircle2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/dialog';
import { useQueueCounts } from '@/features/offline/use-outbox-sync';
import { useSessionState } from './use-session';

/**
 * Quem está operando, sempre visível.
 *
 * Era "trocar de perfil", da época em que entrar era escolher um nome numa lista. Com a
 * entrada por senha (Fase 12), trocar deixou de existir: quem sai **revoga a própria
 * sessão**, e o próximo entra com a credencial dele. É o que faz o "quem alterou?" do
 * histórico valer alguma coisa (RN-130).
 */
export function UserSwitcher() {
  const { session, clearSession } = useSessionState();
  // Os recusados também não subiram: quem sai precisa saber de todos.
  const { pending, rejected } = useQueueCounts();
  const pendentes = pending + rejected;
  const [confirmando, setConfirmando] = useState(false);
  if (session === undefined) return null;

  // Sair com o que foi feito sem sinal ainda na fila (Fase 43, RN-610): nada se apaga — a fila
  // é de quem fez, e sobe quando a mesma pessoa entrar de novo neste aparelho. Mas quem sai
  // precisa saber disso antes, e não depois.
  const sair = () => {
    if (pendentes > 0) setConfirmando(true);
    else void clearSession();
  };

  return (
    <div className="flex min-w-0 items-center gap-1 md:gap-2">
      {/*
        O nome encolhe com reticências; o ícone e o botão de sair, não. Antes o bloco
        inteiro era rígido e, sem espaço, "Administrador" era **cortado** pelo lado — o
        que parece defeito de renderização, e não texto abreviado.
      */}
      {/*
        O nome é o caminho para o próprio cadastro: é onde a pessoa procura quando o que
        está escrito ali é o nome errado dela.
      */}
      <Link
        to="/meu-perfil"
        title="Meu perfil"
        className="flex min-w-0 items-center gap-2 rounded-md bg-(--color-surface-muted) px-2.5 py-1.5 hover:opacity-80 md:px-3"
      >
        <UserCircle2 className="size-4 shrink-0 text-(--color-content-muted)" aria-hidden="true" />
        <span className="truncate text-sm font-medium">{session.userName}</span>
      </Link>
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0 md:h-8 md:w-auto md:px-3"
        onClick={sair}
        title="Sair"
        aria-label="Sair"
      >
        <LogOut className="size-4" aria-hidden="true" />
        <span className="hidden md:inline">Sair</span>
      </Button>
      <ConfirmDialog
        open={confirmando}
        onOpenChange={setConfirmando}
        title="Sair com registros que ainda não subiram?"
        description={`${pendentes === 1 ? 'Um registro feito sem sinal ainda não subiu' : `${String(pendentes)} registros feitos sem sinal ainda não subiram`} para o servidor. Eles ficam guardados neste aparelho e sobem quando você entrar de novo nele.`}
        confirmLabel="Sair mesmo assim"
        onConfirm={() => {
          setConfirmando(false);
          void clearSession();
        }}
      />
    </div>
  );
}
